/**
 * This code was copied from the `main` repository
 * https://github.com/junipersquare/main/blob/a30635ddaa79ab774896191514bdfa4e594b0e7f/jsq/client/src/common/utils/CSRFToken/index.ts
 */
import React from "react";
import { ApolloClient, InMemoryCache, ApolloProvider, ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { createUploadLink } from "apollo-upload-client";
import fetchRetryFactory from "fetch-retry";
const fetchRetry = fetchRetryFactory(fetch);

type Token = string | null;
export let token: Token;
export let tokenPromise: Promise<Token> | null = null;

/**
 * Sets CSRF Token
 *
 * @param newToken
 */
export function setCSRFToken(newToken: Token): void {
  token = newToken;
}

/**
 * Retrieves CSRF Token
 * If the token is already set by setCSRFToken, use it.
 *
 */
export async function getCSRFToken(): Promise<Token> {
  if (token) {
    return Promise.resolve(token);
  }

  if (!tokenPromise) {
    tokenPromise = fetchRetry("/csrf_token", {
      retryDelay: 500,
      retryOn: (attempt, error, response) => {
        // retry at most 2 times (attempt is 0-indexed)
        if (attempt > 1) {
          return false;
        }
        // retry on any network error, or 4xx or 5xx status codes
        if (error !== null || (response && response.status >= 400)) {
          return true;
        }
        return false;
      },
    })
      .then(async (resp) => {
        if (!resp.ok) {
          throw await resp.json();
        }
        return resp;
      })
      .then((resp) => resp.json())
      .then((json: { csrfToken: Token }) => {
        setCSRFToken(json.csrfToken);
        return json.csrfToken;
      })
      .catch((ex) => {
        // Throw the error because we can't do anything about it here
        throw ex;
      });
  }

  return tokenPromise;
}

const csrfMiddleware = setContext(async () => {
  return {
    headers: {
      "X-CSRF-TOKEN": await getCSRFToken(),
      "Apollo-Require-Preflight": "true",
      "x-apollo-operation-name": "UpsertDashboard",
    },
  };
});

// ref: https://www.apollographql.com/docs/react/api/link/apollo-link-retry#default-configuration
const retryLink = new RetryLink({
  delay: {
    initial: Number(process.env.INITIAL_APOLLO_DELAY) || 1000,
    max: Number(process.env.MAX_APOLLO_DELAY) || Infinity,
    jitter: Boolean(process.env.APOLLO_JITTER) || true,
  },
  attempts: {
    max: Number(process.env.MAX_APOLLO_ATTEMPTS) || 3,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    retryIf: (error, _operation) => !!error,
  },
});

const federationGatewayLink = ApolloLink.from([
  csrfMiddleware,
  retryLink,
  createUploadLink({
    credentials: "same-origin",
    uri: "/service/federation/graphql",
  }),
]);

/**
 * Apollo Client configured for file uploads
 *
 * This client is set up with apollo-upload-client to handle file uploads
 * through GraphQL mutations according to the GraphQL multipart request spec.
 */
const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: federationGatewayLink,
});

/**
 * Apollo Provider wrapper component
 *
 * Wraps children with the Apollo Provider using a client configured
 * for file uploads.
 */
export const ApolloWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
